import React, { useState } from 'react';
import { X, Save, FileText, CheckSquare, Zap, Target, Cog, Check } from 'lucide-react';
import { MeetingType } from '../types/sessionContext';

interface MeetingTypeConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (meetingType: Partial<MeetingType>) => void;
  meetingType?: MeetingType | null;
  title: string;
}

export const MeetingTypeConfigModal: React.FC<MeetingTypeConfigModalProps> = ({
  isOpen,
  onClose,
  onSave,
  meetingType,
  title
}) => {
  const [activeTab, setActiveTab] = useState<string>('basic');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState<Partial<MeetingType>>({});
  
  // Convert mainNeeds object to array with stable IDs for better React key management
  const [needsArray, setNeedsArray] = useState<Array<{id: string, key: string, value: string}>>([]);

  // Update form data when meetingType prop changes
  React.useEffect(() => {
    setFormData({
      name: meetingType?.name || '',
      description: meetingType?.description || '',
      dataExtractionFields: meetingType?.dataExtractionFields || '',
      mainNeeds: meetingType?.mainNeeds || {},
      processMapping: meetingType?.processMapping || '',
      toolsIdentification: meetingType?.toolsIdentification || '',
      agendaScript: meetingType?.agendaScript || [],
      successCriteria: meetingType?.successCriteria || [],
      customPrompts: meetingType?.customPrompts || {}
    });
    
    // Convert mainNeeds object to array with stable IDs
    const mainNeeds = meetingType?.mainNeeds || {};
    const newNeedsArray = Object.entries(mainNeeds).map(([key, value], index) => ({
      id: `need-${index}-${key}`, // Stable ID combining index and key
      key,
      value: value || ''
    }));
    setNeedsArray(newNeedsArray);
  }, [meetingType, isOpen]);
  
  // Sync needsArray back to formData.mainNeeds
  React.useEffect(() => {
    const mainNeedsObject: Record<string, string> = {};
    needsArray.forEach(item => {
      if (item.key.trim()) {
        mainNeedsObject[item.key] = item.value;
      }
    });
    
    setFormData(prev => ({
      ...prev,
      mainNeeds: mainNeedsObject
    }));
  }, [needsArray]);

  const tabs = [
    { id: 'basic', name: 'Ogólne', icon: FileText },
    { id: 'data', name: 'Dane', icon: Target },
    { id: 'agenda', name: 'Agenda', icon: CheckSquare },
    { id: 'criteria', name: 'Kryteria', icon: Zap },
    { id: 'prompts', name: 'AI', icon: Cog }
  ];

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(formData);
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
        setIsSaving(false);
      }, 2000);
    } catch (error) {
      setIsSaving(false);
      // Handle error if needed
    }
  };

  const handleSaveAndClose = async () => {
    setIsSaving(true);
    try {
      await onSave(formData);
      setSaveSuccess(true);
      setTimeout(() => {
        onClose();
        setSaveSuccess(false);
        setIsSaving(false);
      }, 1000);
    } catch (error) {
      setIsSaving(false);
      // Handle error if needed
    }
  };

  // Helper function to check if tab is completed
  const isTabCompleted = (tabId: string) => {
    switch (tabId) {
      case 'basic':
        return !!(formData.name?.trim() && formData.description?.trim());
      case 'data':
        return needsArray.some(item => item.key.trim() && item.value.trim());
      case 'agenda':
        return !!(formData.agendaScript && formData.agendaScript.length > 0 && formData.agendaScript.some(item => item.trim()));
      case 'criteria':
        return !!(formData.successCriteria && formData.successCriteria.length > 0 && formData.successCriteria.some(criteria => criteria.trim()));
      case 'prompts':
        return false; // Always incomplete as it's not functional yet
      default:
        return false;
    }
  };


  const handleAddSuccessCriteria = () => {
    const newCriteria = '';
    setFormData({
      ...formData,
      successCriteria: [...(formData.successCriteria || []), newCriteria]
    });
  };

  const handleRemoveSuccessCriteria = (index: number) => {
    const newCriteria = [...(formData.successCriteria || [])];
    newCriteria.splice(index, 1);
    setFormData({
      ...formData,
      successCriteria: newCriteria
    });
  };

  const handleAddAgendaItem = () => {
    const newItem = '';
    setFormData({
      ...formData,
      agendaScript: [...(formData.agendaScript || []), newItem]
    });
  };

  const handleRemoveAgendaItem = (index: number) => {
    const newAgenda = [...(formData.agendaScript || [])];
    newAgenda.splice(index, 1);
    setFormData({
      ...formData,
      agendaScript: newAgenda
    });
  };

  const handleAddNeed = () => {
    const newId = `need-${Date.now()}-${Math.random()}`;
    const newKey = `klucz${needsArray.length + 1}`;
    
    setNeedsArray(prev => [
      ...prev,
      { id: newId, key: newKey, value: '' }
    ]);
  };

  const handleRemoveNeed = (idToRemove: string) => {
    setNeedsArray(prev => prev.filter(item => item.id !== idToRemove));
  };

  const handleUpdateNeedKey = (id: string, newKey: string) => {
    setNeedsArray(prev => prev.map(item => 
      item.id === id ? { ...item, key: newKey } : item
    ));
  };

  const handleUpdateNeedValue = (id: string, newValue: string) => {
    setNeedsArray(prev => prev.map(item => 
      item.id === id ? { ...item, value: newValue } : item
    ));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block w-full max-w-5xl p-0 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-900 shadow-xl rounded-2xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 pb-0 mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {title}
                {meetingType?.name && (
                  <span className="text-blue-600 dark:text-blue-400">: {meetingType.name}</span>
                )}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Skonfiguruj szczegółowe ustawienia dla tego typu spotkania</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors shrink-0"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="mb-0 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <nav className="flex px-6 overflow-x-auto">
              {tabs.map((tab, index) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                const isCompleted = isTabCompleted(tab.id);
                const stepNumber = index + 1;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 py-3 px-3 border-b-2 font-medium text-sm transition-all whitespace-nowrap shrink-0 relative ${
                      isActive
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400 bg-white dark:bg-gray-900 -mb-px'
                        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="relative">
                      {isCompleted ? (
                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      ) : (
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                          isActive 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                        }`}>
                          {stepNumber}
                        </div>
                      )}
                    </div>
                    <Icon className={`w-4 h-4 ${isActive ? 'text-blue-500' : isCompleted ? 'text-green-500' : ''}`} />
                    <span className="font-medium text-xs">{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="space-y-6 max-h-[500px] overflow-y-auto p-6 bg-white dark:bg-gray-900">
            {/* Basic Tab */}
            {activeTab === 'basic' && (
              <div className="space-y-6">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border-l-4 border-blue-500">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-bold">1</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">Ogólne informacje</h4>
                      <p className="text-xs text-blue-700 dark:text-blue-300">Nazwa i opis pomogą zespołowi rozpoznać typ spotkania</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                      Nazwa spotkania *
                    </label>
                    <input
                      type="text"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 focus:border-blue-500 dark:focus:border-blue-400 transition-colors text-base"
                      placeholder="np. Discovery, Kickoff, Brief, Ofertowe, Negocjacje..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                      Opis spotkania
                    </label>
                    <textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 focus:border-blue-500 dark:focus:border-blue-400 transition-colors text-base resize-none"
                      placeholder="Krótko opisz cel i kontekst tego typu spotkania..."
                    />
                  </div>
                </div>

              </div>
            )}


            {/* Data Tab - Key-Value Dictionary, CRM Mapping, and Process */}
            {activeTab === 'data' && (
              <div className="space-y-6">
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-xl border-l-4 border-green-500">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-bold">2</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-1">Dane do analizy</h4>
                      <p className="text-xs text-green-700 dark:text-green-300">Słownik kluczy-wartości wykorzystywany przez AI podczas analizy</p>
                    </div>
                  </div>
                </div>

                {/* Key-Value Dictionary Section */}
                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border-2 border-gray-200 dark:border-gray-700 shadow-sm">
                  <div className="mb-4">
                    <h5 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Słownik atrybutów</h5>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Określ kluczowe atrybuty w formacie klucz : wartość do identyfikacji przez AI
                    </p>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h6 className="font-medium text-gray-900 dark:text-gray-100">Atrybuty do rozpoznania</h6>
                      <button
                        onClick={handleAddNeed}
                        className="px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center space-x-2 shadow-sm"
                      >
                        <span>+</span>
                        <span>Dodaj atrybut</span>
                      </button>
                    </div>

                    <div className="space-y-3">
                      {needsArray.map((item) => (
                        <div key={item.id} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600">
                          <input
                            type="text"
                            value={item.key}
                            onChange={(e) => handleUpdateNeedKey(item.id, e.target.value)}
                            className="w-1/3 px-3 py-2 border-2 border-transparent bg-white dark:bg-gray-600 rounded-lg focus:border-green-500 transition-colors text-sm font-medium"
                            placeholder="klucz"
                          />
                          <span className="text-gray-400 font-bold">:</span>
                          <input
                            type="text"
                            value={item.value}
                            onChange={(e) => handleUpdateNeedValue(item.id, e.target.value)}
                            className="flex-1 px-3 py-2 border-2 border-transparent bg-white dark:bg-gray-600 rounded-lg focus:border-green-500 transition-colors text-sm"
                            placeholder="wartość"
                          />
                          <button
                            onClick={() => handleRemoveNeed(item.id)}
                            className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                            title="Usuń atrybut"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>

                    {needsArray.length === 0 && (
                      <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                        <p className="text-sm">Brak zdefiniowanych atrybutów</p>
                        <p className="text-xs mt-1">Kliknij "Dodaj atrybut" aby rozpocząć</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Agenda/Script Tab */}
            {activeTab === 'agenda' && (
              <div className="space-y-6">
                <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-xl border-l-4 border-indigo-500">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-bold">3</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-indigo-900 dark:text-indigo-100 mb-1">Agenda i punkty kontrolne</h4>
                      <p className="text-xs text-indigo-700 dark:text-indigo-300">Zdefiniuj kluczowe elementy, na które AI ma zwrócić uwagę podczas analizy</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h5 className="font-medium text-gray-900 dark:text-gray-100">Lista punktów kontrolnych</h5>
                    <button
                      onClick={handleAddAgendaItem}
                      className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center space-x-2 shadow-sm"
                    >
                      <span>+</span>
                      <span>Dodaj punkt</span>
                    </button>
                  </div>

                  <div className="space-y-3">
                    {formData.agendaScript?.map((item, index) => (
                      <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                        <div className="flex-shrink-0 w-6 h-6 bg-indigo-200 dark:bg-indigo-800 rounded-full flex items-center justify-center">
                          <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">{index + 1}</span>
                        </div>
                        <input
                          type="text"
                          value={item}
                          onChange={(e) => {
                            const newAgenda = [...(formData.agendaScript || [])];
                            newAgenda[index] = e.target.value;
                            setFormData({ ...formData, agendaScript: newAgenda });
                          }}
                          className="flex-1 px-4 py-2 border-2 border-transparent bg-white dark:bg-gray-700 rounded-lg focus:border-indigo-500 transition-colors"
                          placeholder="np. Przedstawił się i firmę, Zadawał otwarte pytania..."
                        />
                        <button
                          onClick={() => handleRemoveAgendaItem(index)}
                          className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                          title="Usuń punkt"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {(!formData.agendaScript || formData.agendaScript.length === 0) && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <p className="text-sm">Brak zdefiniowanych punktów kontrolnych</p>
                      <p className="text-xs mt-1">Kliknij "Dodaj punkt" aby rozpocząć</p>
                    </div>
                  )}

                  <div className="mt-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border-l-4 border-indigo-500">
                    <p className="text-xs text-indigo-700 dark:text-indigo-300">
                      🎯 <strong>Wskazówka:</strong> Używaj konkretnych, mierzalnych punktów kontrolnych. Przykłady: "Przywitał się i przedstawił firmę", "Zadawał otwarte pytania", "Podsumował ustalone punkty".
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Success Criteria Tab */}
            {activeTab === 'criteria' && (
              <div className="space-y-6">
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-xl border-l-4 border-purple-500">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-bold">4</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-purple-900 dark:text-purple-100 mb-1">Kryteria oceny</h4>
                      <p className="text-xs text-purple-700 dark:text-purple-300">Określ co AI ma sprawdzić aby ocenić jakość spotkania</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h5 className="font-medium text-gray-900 dark:text-gray-100">Lista kryteriów</h5>
                    <button
                      onClick={handleAddSuccessCriteria}
                      className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center space-x-2 shadow-sm"
                    >
                      <span>+</span>
                      <span>Dodaj kryterium</span>
                    </button>
                  </div>

                  <div className="space-y-3">
                    {formData.successCriteria?.map((criteria, index) => (
                      <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                        <div className="flex-shrink-0 w-6 h-6 bg-purple-200 dark:bg-purple-800 rounded-full flex items-center justify-center">
                          <span className="text-xs font-medium text-purple-700 dark:text-purple-300">{index + 1}</span>
                        </div>
                        <input
                          type="text"
                          value={criteria}
                          onChange={(e) => {
                            const newCriteria = [...(formData.successCriteria || [])];
                            newCriteria[index] = e.target.value;
                            setFormData({ ...formData, successCriteria: newCriteria });
                          }}
                          className="flex-1 px-4 py-2 border-2 border-transparent bg-white dark:bg-gray-700 rounded-lg focus:border-purple-500 transition-colors"
                          placeholder="np. Przedstawił się i firmę, Zadawał otwarte pytania..."
                        />
                        <button
                          onClick={() => handleRemoveSuccessCriteria(index)}
                          className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                          title="Usuń kryterium"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {(!formData.successCriteria || formData.successCriteria.length === 0) && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <p className="text-sm">Brak zdefiniowanych kryteriów</p>
                      <p className="text-xs mt-1">Kliknij "Dodaj kryterium" aby rozpocząć</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Custom Prompts Tab */}
            {activeTab === 'prompts' && (
              <div className="space-y-6">
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border-l-4 border-gray-400">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-bold">5</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Zaawansowane prompty AI</h4>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Dostosuj sposób analizy AI do specyfiki tego typu spotkania</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 p-6 rounded-xl border-2 border-yellow-200 dark:border-yellow-800 shadow-sm">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-xl flex items-center justify-center shadow-md">
                        <span className="text-2xl">🚧</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-yellow-900 dark:text-yellow-100 mb-2">
                        Funkcja w przygotowaniu
                      </h4>
                      <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-4 leading-relaxed">
                        Ta zaawansowana sekcja pozwoli na pełne dostosowanie algorytmów AI do specyficznych potrzeb każdego typu spotkania. 
                        Będzie można konfigurować:
                      </p>
                      <ul className="text-sm text-yellow-800 dark:text-yellow-200 space-y-2">
                        <li className="flex items-center space-x-2">
                          <span className="w-1.5 h-1.5 bg-yellow-600 rounded-full"></span>
                          <span>Personalizowane prompty dla różnych faz analizy</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="w-1.5 h-1.5 bg-yellow-600 rounded-full"></span>
                          <span>Dostosowane wagi dla poszczególnych kryteriów oceny</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="w-1.5 h-1.5 bg-yellow-600 rounded-full"></span>
                          <span>Specjalistyczne słownictwo branżowe</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <span className="w-1.5 h-1.5 bg-yellow-600 rounded-full"></span>
                          <span>Zaawansowane modele oceny emocji</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border-2 border-gray-200 dark:border-gray-700 shadow-sm opacity-60">
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-600 dark:text-gray-400 mb-3">
                      🤖 Prompt analizy AI (niedostępne)
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mb-4">
                      Tutaj będzie można wprowadzić dostosowane instrukcje dla AI dotyczące analizy tego typu spotkania
                    </p>
                  </div>
                  
                  <textarea
                    value=""
                    rows={6}
                    disabled
                    className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-base resize-none cursor-not-allowed opacity-50"
                    placeholder="Przykład przyszłego promptu:

Analizując to spotkanie typu 'Prezentacja oferty', zwróć szczególną uwagę na:
- Sposób prezentacji kluczowych korzyści produktu
- Reakcje klienta na poszczególne elementy oferty  
- Identyfikację głównych obiekcji i sposoby ich obsługi
- Techniki budowania wartości i uzasadniania ceny
- Effective closing techniques used during presentation..."
                  />

                  <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      ⏳ Ta funkcja zostanie udostępniona w przyszłej aktualizacji systemu
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center px-6 py-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-4">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                💡 Zmiany zostaną zapisane automatycznie
              </div>
              
              {/* Progress indicator */}
              <div className="hidden sm:flex items-center space-x-2">
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  Postęp:
                </div>
                <div className="flex space-x-1">
                  {tabs.slice(0, -1).map((tab, index) => { // Exclude prompts tab from progress
                    const isCompleted = isTabCompleted(tab.id);
                    return (
                      <div
                        key={tab.id}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          isCompleted ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                        title={`${tab.name} - ${isCompleted ? 'Completed' : 'Incomplete'}`}
                      />
                    );
                  })}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {tabs.slice(0, -1).filter(tab => isTabCompleted(tab.id)).length}/{tabs.length - 1}
                </div>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Anuluj
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className={`px-6 py-2 text-sm font-medium text-white rounded-xl shadow-sm hover:shadow-md transition-all flex items-center space-x-2 min-w-[120px] justify-center ${
                  saveSuccess 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-blue-600 hover:bg-blue-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Zapisywanie...</span>
                  </>
                ) : saveSuccess ? (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Zapisano!</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Zapisz</span>
                  </>
                )}
              </button>
              <button
                onClick={handleSaveAndClose}
                disabled={!formData.name?.trim() || isSaving}
                className={`px-4 py-2 text-sm font-medium text-white rounded-xl shadow-sm hover:shadow-md transition-all flex items-center space-x-2 min-w-[140px] justify-center ${
                  saveSuccess 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-gray-600 hover:bg-gray-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <X className="w-4 h-4" />
                <span>Zapisz i zamknij</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};